import React, { useEffect, useRef, useState } from 'react';
import { useEsMovil } from '../hooks/useEsMovil';

export interface ComboOption {
  value: string;
  label: string;
}

export interface ComboProps {
  label: string;
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  accent?: boolean;
  placeholder?: string;
  /**
   * Variante "flat" (spec §2): para vivir DENTRO del panel de filtros
   * (`Filtros`/`DrillTienda`) sin verse como card-sobre-card — sin fondo,
   * sin borde ni sombra, radio 9px y padding compacto. El resto del control
   * (label, valor, chevron, panel de búsqueda, opciones) no cambia.
   */
  flat?: boolean;
}

/**
 * Dropdown buscable, recreado pixel-exact de `Combo.dc.html` (design
 * handoff). Botón que muestra `label` + valor actual; al hacer clic abre un
 * panel con un input de búsqueda (autofocus), lista filtrada en vivo
 * (contains, case-insensitive), clic en una opción selecciona y cierra, y un
 * backdrop `position:fixed` que cierra al hacer clic fuera.
 */
const Combo: React.FC<ComboProps> = ({
  label,
  value,
  options,
  onChange,
  accent = false,
  placeholder = 'Buscar…',
  flat = false,
}) => {
  const esMovil = useEsMovil();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const [btnHover, setBtnHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      // El input se monta junto con el panel al abrir: enfocar en el mismo
      // ciclo de render (después de que el DOM ya lo insertó).
      inputRef.current?.focus();
    }
  }, [open]);

  const toggle = () => {
    setOpen((prev) => !prev);
    setQ('');
  };

  const close = () => {
    setOpen(false);
    setQ('');
  };

  // Escape cierra el panel, igual que en `MultiCombo`.
  useEffect(() => {
    if (!open) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [open]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  const labelColor = accent ? '#22349b' : '#9298a3';
  const borderColor = accent ? '#dfe4f2' : '#e7e9ef';

  // Estilo del botón según variante (spec §2): "flat" (dentro del panel) vs
  // "normal" (suelto). El hover cambia solo el fondo (#f4f6fa flat / #fbfcfe
  // normal); el resto del contenido es idéntico.
  const buttonStyle: React.CSSProperties = flat
    ? {
        width: '100%',
        textAlign: 'left',
        background: btnHover ? '#f4f6fa' : 'transparent',
        border: 'none',
        borderRadius: 9,
        padding: '3px 2px',
        boxShadow: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'block',
      }
    : {
        width: '100%',
        textAlign: 'left',
        background: btnHover ? '#fbfcfe' : '#fff',
        border: `1px solid ${borderColor}`,
        borderRadius: 13,
        padding: '7px 13px',
        boxShadow: '0 1px 2px rgba(20,28,54,.04)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'block',
      };

  const current = options.find((o) => String(o.value) === String(value));
  const currentLabel = current ? current.label : value || '—';

  const needle = q.toLowerCase();
  const filtered = options.filter((o) =>
    String(o.label).toLowerCase().includes(needle),
  );

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={toggle}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        style={buttonStyle}
      >
        <div
          style={{
            // Regla 5 de la adaptacion a movil: ninguna etiqueta por debajo
            // de 11 px. Este rotulo ("REGIONAL", "SUPERVISOR", ...) va en
            // versalitas con `letterSpacing`, que a 10 px en un telefono es lo
            // primero que se vuelve ilegible. En escritorio se queda en 10.
            fontSize: esMovil ? 11 : 10,
            letterSpacing: '.09em',
            textTransform: 'uppercase',
            color: labelColor,
            fontWeight: 800,
          }}
        >
          {label}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 3,
            gap: 8,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: '#14171d',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentLabel}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke={labelColor}
            strokeWidth={2.4}
            strokeLinecap="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      {open && (
        <>
          <div
            onClick={close}
            style={{ position: 'fixed', inset: 0, zIndex: 60 }}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              minWidth: '100%',
              width: 'max-content',
              maxWidth: 340,
              background: '#fff',
              border: '1px solid #e2e5ee',
              borderRadius: 12,
              boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
              zIndex: 61,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '9px 10px', borderBottom: '1px solid #f0f2f6' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  background: '#f4f6fa',
                  border: '1px solid #e7e9ef',
                  borderRadius: 9,
                  padding: '7px 10px',
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9298a3"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4-4" />
                </svg>
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={placeholder}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    width: '100%',
                    color: '#14171d',
                  }}
                />
              </div>
            </div>
            <div
              data-testid="combo-options"
              style={{ maxHeight: 264, overflow: 'auto', padding: 5 }}
            >
              {filtered.map((o) => {
                const selected = String(o.value) === String(value);
                const hovered = hoveredValue === o.value;
                const bg = hovered ? '#f2f4f8' : selected ? '#eef1fb' : 'transparent';
                const color = selected ? '#22349b' : '#2b3350';
                const weight = selected ? 800 : 600;
                return (
                  <div
                    key={o.value}
                    onClick={() => pick(o.value)}
                    onMouseEnter={() => setHoveredValue(o.value)}
                    onMouseLeave={() => setHoveredValue(null)}
                    style={{
                      padding: '8px 11px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 13.5,
                      fontWeight: weight,
                      color,
                      background: bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>{o.label}</span>
                    {selected && (
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22349b"
                        strokeWidth={2.6}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12l5 5L20 6" />
                      </svg>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: '12px 11px',
                    fontSize: 13,
                    color: '#9298a3',
                    fontWeight: 600,
                  }}
                >
                  Sin resultados
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Combo;
